"use client";
import { useRouter } from "@/lib/useRouter";
import { useEffect, useRef, useState } from "react";

type QueryPrimitive = string | number | Date;
type QueryValue = QueryPrimitive | QueryPrimitive[];
type QueryParams = Record<string, QueryValue | null | undefined>;

const parseValue = (val: string): string | number | Date => {
  /*if (val.includes("T")) {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) return d;
  }*/
  const n = Number(val);
  return Number.isNaN(n) ? val : n;
};

export const parseQueryObject = (
  query: Record<string, string | string[] | undefined>
): Record<string, QueryPrimitive | QueryPrimitive[]> => {
  const result: Record<string, QueryPrimitive | QueryPrimitive[]> = {};

  for (const key in query) {
    const value = query[key];
    if (value === undefined) continue;

    if (Array.isArray(value)) {
      result[key] = value.map(parseValue);
    } else {
      result[key] = parseValue(value);
    }
  }

  return result;
};

const buildQueryString = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();

  for (const key in params) {
    const raw = params[key];
    if (raw === undefined || raw === null) continue;

    const encode = (v: QueryPrimitive) =>
      v instanceof Date ? v.toISOString() : String(v);

    if (Array.isArray(raw)) {
      raw.forEach((v) => searchParams.append(key, encode(v)));
    } else {
      searchParams.set(key, encode(raw));
    }
  }

  return searchParams.toString();
};

export const useUrlQuerySync = (
  queryObject: QueryParams,
  onLoad: (
    paramsFromUrl: Record<string, QueryPrimitive | QueryPrimitive[]>
  ) => void,
  enabled = true,
  useWindowReplace = false,
  preserveUnknownParams = false
): Record<string, QueryPrimitive | QueryPrimitive[]> => {
  const router = useRouter();
  const isFirstLoad = useRef(true);
  const [parsedParams, setParsedParams] = useState<
    Record<string, QueryPrimitive | QueryPrimitive[]>
  >({});

  // Track last query string to avoid unnecessary router.replace
  const lastQueryStringRef = useRef<string>("");

  // On first load, parse and trigger onLoad
  useEffect(() => {
    if (!router.isReady || !enabled) return;
    const parsed = parseQueryObject(router.query);
    setParsedParams(parsed);
    onLoad(parsed);

    // Save the initial query string to prevent unnecessary replace
    const initialQueryString = buildQueryString(parsed);
    lastQueryStringRef.current = initialQueryString;
  }, [router.isReady, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // When queryObject changes, update URL if query string differs
  useEffect(() => {
    if (!router.isReady || !enabled) {
      return;
    }

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    let paramsToWrite: QueryParams;

    if (preserveUnknownParams) {
      // Start from the current URL params
      const currentQuery = parseQueryObject(router.query);
      paramsToWrite = { ...currentQuery };

      for (const key in queryObject) {
        const value = queryObject[key];
        if (value === null || value === undefined) {
          delete paramsToWrite[key];
        } else {
          paramsToWrite[key] = value;
        }
      }
    } else {
      // Only use queryObject keys
      paramsToWrite = {};

      for (const key in queryObject) {
        const value = queryObject[key];
        if (value !== null && value !== undefined) {
          paramsToWrite[key] = value;
        }
      }
    }

    const newQueryString = buildQueryString(paramsToWrite);
    if (newQueryString === lastQueryStringRef.current) return;

    const path = `${router.pathname}${newQueryString ? `?${newQueryString}` : ""}`;
    useWindowReplace
      ? window.history.replaceState({}, "", path)
      : router.replace(path, undefined, { shallow: true });

    lastQueryStringRef.current = newQueryString;
  }, [
    queryObject,
    router.pathname,
    router.isReady,
    enabled,
    preserveUnknownParams
  ]);

  return parsedParams;
};
